import { useCallback, useEffect, useRef, useState } from 'react';
import { PreferenceClient } from '../client/preference.client';
import { Context, CustomFeature, FilterDefinition, FilterType, Preference, PreferenceName, ValueSet } from '../types';
import {
  CustomNumericalClass,
  SavedCustomFeatureSegments,
  CustomCategoricalClass,
} from '../types/content/custom.types';
import { ContextManager } from '../context/manager';
import { isViewer } from './utils';
import { useUniquePreferences } from '../context/unique-preferences/unique-preferences.provider';

/**
 * Hook to save and load the saved custom segmentation
 * from the preference client
 * @returns - the list of saved custom segmentation and the functions to save, delete and load the custom segmentations
 */
export const useSaveFeatureSegments = () => {
  const preferencesFeatureSegments = PreferenceName.segment_feature;
  const [currentSelectedCell, setCurrentSelectedCell] = useState<string[]>(undefined);
  const [applicableSavedFeatureSegments, setApplicableSavedFeatureSegments] = useState<SavedCustomFeatureSegments[]>(
    []
  );
  const [unapplicableSavedFeatureSegments, setUnapplicableSavedFeatureSegments] = useState<
    SavedCustomFeatureSegments[]
  >([]);

  const { allUniquePreferences, refreshPreferences, updatePreferences, deletePreferences } = useUniquePreferences();
  const allUniquePreferencesRef = useRef(allUniquePreferences);

  useEffect(() => {
    allUniquePreferencesRef.current = allUniquePreferences;
  }, [allUniquePreferences]);

  /*
    Here we will need to listen to context changes to update the list of saved custom segmentations.
    We will need to call loadSavedCustomSegmentation when the context changes as it depends on the
    current cell being applied.
    */
  useEffect(() => {
    const subscriber = async (context: Context) => {
      if (!context) {
        return;
      }

      const settings = await ContextManager.getInstance().getSettings(['cell']);
      const cells: string[] = Array.isArray(settings?.cell?.values) ? settings.cell.values.map(cell => cell.value) : [];

      setCurrentSelectedCell(cells);

      const section = context.filters.sections;
      const featureList = section?.find(item => item?.label === 'Features')?.ungrouped?.definitions;

      if (featureList) {
        await loadSavedCustomFeatureSegments(featureList);
      }
    };

    ContextManager.getInstance().subscribeToSettingsChanges(subscriber);
    return () => ContextManager.getInstance().unsubscribeFromSettingsChanges(subscriber);
  }, []);

  useEffect(() => {
    refreshPreferences();
  }, [refreshPreferences, applicableSavedFeatureSegments, unapplicableSavedFeatureSegments]);

  /**
   * Saves the custom feature segments to preference client
   * @param name Name of the custom segmentation
   * @param desc Description of the custom segmentation
   * @param customSegmentation The custom segmentation to save
   * @param min the minimum value within the allowed range of the custom segmentation
   * @param max the maximum value within the allowed range of the custom segmentation
   */
  const saveCustomFeatureSegments = (
    name: string,
    desc: string,
    featureSegmentsLabel: string,
    customSegmentation: string,
    filterType: FilterType,
    min?: number,
    max?: number
  ) => {
    const customFeatSegmentsToSave: ValueSet = {};
    customFeatSegmentsToSave[name] = convertSavedCustomFeatureSegmentsToPreferenceData(
      featureSegmentsLabel,
      desc,
      customSegmentation,
      filterType,
      min,
      max
    );
    PreferenceClient.getInstance()
      .createNewUnique(preferencesFeatureSegments, customFeatSegmentsToSave)
      .then(response => {
        if (response) {
          const newCustomFeatureSegments = convertPreferenceDataToSavedCustomFeatSeg(response);
          setApplicableSavedFeatureSegments([...applicableSavedFeatureSegments, newCustomFeatureSegments]);
          updatePreferences(response, PreferenceName.segment_feature);
        }
        //update the provider
      });
  };

  /**
   * Convert the response from the preference client to SavedCustomFeatureSegments object
   * @param data - the data retrieved from the preference client
   * @returns - the SavedCustomFeatureSegments object
   */
  const convertPreferenceDataToSavedCustomFeatSeg = (data: Preference): SavedCustomFeatureSegments => {
    if (!data.data || Object.keys(data.data).length === 0) {
      return undefined;
    }

    const prefName = Object.keys(data.data)[0];
    const prefData = data.data[prefName];
    const cell = prefData.cell;
    const prefCustomFeatSeg = prefData.customSegmentation[0].split('|');
    const prefCustomFeatSegLabel = prefData.segmentationLabel[0];
    const description = prefData.description[0];
    const maxRange = prefData.max[0];
    const featSegmentName = prefCustomFeatSeg[0].split(':')[1];
    const destructuredCustomFeatSeg = prefCustomFeatSeg.slice(1);
    const filterType = FilterType[prefData.filterType?.[0] as keyof typeof FilterType] || FilterType.numericRange; //default to numeric range to handle existing saved filter without filter type
    const isNumericRange = filterType === FilterType.numericRange;
    const customFeatSegmentArray: CustomNumericalClass[] | CustomCategoricalClass[] = destructuredCustomFeatSeg.map(
      (custSeg, index, list) => {
        const [strKey, strLabel] = custSeg.split(':');
        return {
          min: isNumericRange ? Number(strKey) : undefined,
          label: strLabel,
          max: isNumericRange
            ? index === list.length - 1
              ? Number(maxRange)
              : Number(list[index + 1].split(':')[0])
            : undefined,
          selectedOptions: !isNumericRange ? strKey : undefined, //for categorical only
          options: undefined, //for categorical only pending handling
        };
      }
    );

    const savedCustomFeatSeg: SavedCustomFeatureSegments = {
      name: prefName,
      segmentationName: featSegmentName,
      segmentationLabel: prefCustomFeatSegLabel,
      description,
      customSegmentation: customFeatSegmentArray,
      preferenceId: data.id,
      createdBy: data.created_by,
      cells: cell,
      filterType: filterType,
    };
    return savedCustomFeatSeg;
  };

  /**
   * Convert the values for saved custom feature segment to from the preference data format
   * @param featureSegmentsLabel the label of the custom feature segment
   * @param desc the description of the saved custom segmentation
   * @param customFeatSegTiers the tiers of the custom feature segment
   * @param min lowest value of the tier
   * @param max highest value of the tier
   * @returns the converted data
   */
  const convertSavedCustomFeatureSegmentsToPreferenceData = (
    featureSegmentsLabel: string,
    desc: string,
    customFeatSegTiers: string,
    filterType: FilterType,
    min: number,
    max: number
  ) => {
    return {
      description: [desc],
      customSegmentation: [customFeatSegTiers],
      //min and max is need for conversion upon retrieval
      min: [min?.toString()],
      max: [max?.toString()],
      cell: currentSelectedCell,
      segmentationLabel: [featureSegmentsLabel],
      filterType: [filterType],
    };
  };

  /**
   * Deletes the saved custom feature segments in preference client
   * @param customFeatSegToDelete - the saved custom feature segments to delete
   * @param userId
   */
  const deleteSavedCustomFeatureSegments = async (
    customFeatSegToDelete: SavedCustomFeatureSegments,
    userId: string,
    origin: string
  ) => {
    const contextData = ContextManager.getInstance().getByNameFromActiveContext('customFeature') as CustomFeature;
    const newData = { ...contextData };

    Object.keys(newData).forEach(key => {
      if (newData[key]?.[origin]?.selectedFeature?.label === customFeatSegToDelete.name) {
        delete newData[key].selectedFeature;
      }
    });

    ContextManager.getInstance().updateCustomFeatureValues({ customFeature: newData });
    // if the user is the viewer, delete the permissions, otherwise delete the whole preference
    if (isViewer(customFeatSegToDelete.createdBy, userId)) {
      await PreferenceClient.getInstance().deletePermissions(
        preferencesFeatureSegments,
        customFeatSegToDelete.preferenceId,
        userId
      );
    } else {
      await PreferenceClient.getInstance().delete(preferencesFeatureSegments, customFeatSegToDelete.preferenceId);
    }
    //delete in the provider
    deletePreferences(customFeatSegToDelete.preferenceId, PreferenceName.segment_feature);
    const updatedSavedCustomFeatSeg = applicableSavedFeatureSegments.filter(
      customFeatSeg => customFeatSeg.preferenceId !== customFeatSegToDelete.preferenceId
    );
    const updatedUnapplicableSavedCustomFeatSeg = unapplicableSavedFeatureSegments.filter(
      customFeatSeg => customFeatSeg.preferenceId !== customFeatSegToDelete.preferenceId
    );
    setApplicableSavedFeatureSegments(updatedSavedCustomFeatSeg);
    setUnapplicableSavedFeatureSegments(updatedUnapplicableSavedCustomFeatSeg);
  };

  /**
   * Loads the saved custom feature segments from the preference client
   * @param featureList
   */
  const loadSavedCustomFeatureSegments = useCallback(
    async (featureList: FilterDefinition[]) => {
      const preferenceSegments = allUniquePreferencesRef.current[preferencesFeatureSegments] || [];
      const applicableSavedFeatSegArray: SavedCustomFeatureSegments[] = [];
      const unApplicableSavedFeatSegArray: SavedCustomFeatureSegments[] = [];

      if (!preferenceSegments) {
        return;
      }

      //convert from preference to savedCustomSegmentation list
      preferenceSegments.forEach(preference => {
        const featureSegmentation = convertPreferenceDataToSavedCustomFeatSeg(preference);

        if (!featureSegmentation) {
          return;
        }

        const isApplicableSavedFeatures = featureList.some(
          feature => feature.name === featureSegmentation.segmentationName
        );

        //Check if the cells is applicable in this selected context
        if (isApplicableSavedFeatures) {
          applicableSavedFeatSegArray.push(featureSegmentation);
        } else {
          unApplicableSavedFeatSegArray.push(featureSegmentation);
        }
      });

      setApplicableSavedFeatureSegments(applicableSavedFeatSegArray);
      setUnapplicableSavedFeatureSegments(unApplicableSavedFeatSegArray);
    },
    [allUniquePreferences]
  );

  return {
    applicableSavedFeatureSegments,
    unapplicableSavedFeatureSegments,
    loadSavedCustomFeatureSegments,
    saveCustomFeatureSegments,
    deleteSavedCustomFeatureSegments,
  };
};
